/* eslint-disable no-console */
import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import { AcresFeatureFlags } from '@/constants/constants';
import useParcelsToBoundaries from '@/features/boundaries/util/parcelsToBoundaries';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import { useToolbarContext } from '@/features/toolbar/provider';
import { useGetFeatureFlag } from '@/hooks/useGetFeatureFlag';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { useToastContext } from '@/providers/ToastProvider';
import { useToolTipContext } from '@/providers/ToolTipProvider';
import { RootState } from '@/redux/reducers';

import { configIdMapEdit, ShapeEditIds } from '../config';
import useBoundaryLayer from '../hooks/useBoundaryLayer';
import { useBoundaryContext } from '../provider';

type Props = {
  editMode?: ShapeEditIds | null;
};

const ConvertLand: FC<Props> = ({ editMode = null }: Props) => {
  const isToolbarActive = useGetFeatureFlag(
    AcresFeatureFlags.TEMP_TOOL_TOP_BAR
  );
  const { clearToolTip, setText, setIsVisible } = useToolTipContext();
  const { selectedFeatures: oldSelectedFeatures, setEditMode } =
    useBoundaryContext();
  const { combineAdjacentFeaturesV2, convertToBoundaries } =
    useParcelsToBoundaries();

  const { clearToastContent } = useToastContext();

  const { mapInstance: map } = useMapboxContext();
  const { drawTool: drawToolRedux } = useSelector(
    (state: RootState) => state.drawV2
  );
  const { selectedSubjectProperties } = useSelectionsPanelContext();

  const {
    drawTool: toolbarDrawTool,
    boundariesSelectedFeatures,
    selectedTool,
    resetToDefaultTool,
  } = useToolbarContext();

  const toolMode = useMemo(() => {
    if (isToolbarActive) return selectedTool.boundaryMode ?? null;

    return editMode;
  }, [editMode, isToolbarActive, selectedTool.boundaryMode]);

  const selectedFeatures = useMemo(() => {
    if (isToolbarActive) return boundariesSelectedFeatures;

    return oldSelectedFeatures;
  }, [boundariesSelectedFeatures, isToolbarActive, oldSelectedFeatures]);

  const drawTool = useMemo(() => {
    if (isToolbarActive) {
      return toolbarDrawTool;
    }

    return drawToolRedux;
  }, [drawToolRedux, isToolbarActive, toolbarDrawTool]);

  useBoundaryLayer(map, toolMode);

  const closePopup = useCallback(() => {
    setEditMode(null);
    clearToastContent();
    drawTool?.changeMode('simple_select');
  }, [clearToastContent, drawTool, setEditMode]);

  const handleOnClick = useCallback(
    (useSelectionsPanel = false) => {
      if (!toolMode) return;

      if (toolMode === ShapeEditIds.MERGE_SHAPES) {
        if (useSelectionsPanel) {
          combineAdjacentFeaturesV2(selectedSubjectProperties);
        } else {
          combineAdjacentFeaturesV2(selectedFeatures);
        }
      } else {
        convertToBoundaries(selectedFeatures);
      }

      closePopup();

      if (isToolbarActive) {
        resetToDefaultTool();
      }
    },
    [
      toolMode,
      closePopup,
      isToolbarActive,
      combineAdjacentFeaturesV2,
      selectedSubjectProperties,
      selectedFeatures,
      convertToBoundaries,
      resetToDefaultTool,
    ]
  );

  const handleOnEntry = useCallback(() => {
    if (!toolMode) return;

    clearToolTip();

    if (toolMode === ShapeEditIds.MERGE_SHAPES) {
      if (!selectedFeatures || selectedFeatures.length < 2) {
        setIsVisible(true);
        setText('Click the polygons you would like to merge.');
      }
    } else if (!selectedFeatures || selectedFeatures.length < 1) {
      setIsVisible(true);
      setText('Make at least one selection to convert');
    } else {
      setIsVisible(false);
    }
  }, [toolMode, selectedFeatures]);

  const handleOnLeave = useCallback(() => {
    if (!toolMode) return;
    setIsVisible(true);
    setText(configIdMapEdit[toolMode].toolTip);
  }, [toolMode]);

  const buttonText = useMemo(() => {
    if (!toolMode) return null;
    if (toolMode === ShapeEditIds.MERGE_SHAPES) {
      return selectedFeatures && selectedFeatures.length > 1
        ? `Merge Selected (${selectedFeatures.length})`
        : 'Merge Selected';
    }

    return selectedFeatures.length < 2
      ? 'Convert to Boundary'
      : 'Convert to Boundaries';
  }, [toolMode, selectedFeatures.length]);

  const secondaryButtonText = useMemo(() => {
    if (!toolMode) return null;
    if (
      toolMode === ShapeEditIds.MERGE_SHAPES &&
      selectedSubjectProperties.length > 0
    ) {
      return selectedSubjectProperties.length > 1
        ? `Merge Boundaries (${selectedSubjectProperties.length})`
        : 'Merge Boundaries';
    }

    return null;
  }, [selectedSubjectProperties.length, toolMode]);

  const isSecondaryButtonDisabled = useMemo(() => {
    if (!toolMode || toolMode !== ShapeEditIds.MERGE_SHAPES) return true;

    return !selectedSubjectProperties || selectedSubjectProperties.length < 2;
  }, [selectedSubjectProperties, toolMode]);

  const isDisabled = useMemo(() => {
    if (!toolMode) return true;
    if (toolMode === ShapeEditIds.MERGE_SHAPES) {
      return !selectedFeatures || selectedFeatures.length < 2;
    }

    // Converting to boundary requires at least one selection
    return !selectedFeatures || selectedFeatures.length < 1;
  }, [toolMode, selectedFeatures]);

  useEffect(() => {
    if (
      toolMode !== ShapeEditIds.MERGE_SHAPES &&
      toolMode !== ShapeEditIds.CONVERT_SHAPE &&
      isToolbarActive
    )
      closePopup();
  }, [closePopup, isToolbarActive, toolMode]);

  if (
    toolMode !== ShapeEditIds.MERGE_SHAPES &&
    toolMode !== ShapeEditIds.CONVERT_SHAPE
  )
    return null;

  return (
    <div
      className={twMerge(
        'left-[50%] top-16 z-80 m-auto flex w-max flex-col items-center bg-gray-90 px-4 py-3',
        isToolbarActive && 'top-[125px]'
      )}
      onMouseEnter={handleOnEntry}
      onMouseLeave={handleOnLeave}
    >
      <Button
        className="w-full"
        disabled={isDisabled}
        size={ButtonSize.SMALL}
        variant={ButtonVariant.PRIMARY}
        onClick={() => handleOnClick()}
      >
        {configIdMapEdit[toolMode].icon && (
          <div className="pr-2">{configIdMapEdit[toolMode].icon}</div>
        )}

        {buttonText}
      </Button>
      {secondaryButtonText && (
        <Button
          className="mt-3 w-full"
          disabled={isSecondaryButtonDisabled}
          size={ButtonSize.SMALL}
          variant={ButtonVariant.SECONDARY_WHITE}
          onClick={() => handleOnClick(true)}
        >
          <div className="pr-2">{configIdMapEdit[toolMode].icon}</div>

          {secondaryButtonText}
        </Button>
      )}
    </div>
  );
};

export default memo(ConvertLand);
